import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';

import { Wrapper } from './Wrapper';
import { GET_IS_GRACE_PERIOD } from '@global/requests/subscriptionDetails';
import { Spinner } from '@global/components/UI Elements/Spinner';

import analytic from '@global/utils/AdobeAnalytics';
import { OrderItemType } from '@src/global/utils/orderUtils';
import { SiteConfig } from '@global/utils/SiteConfig';
import { CancellationReasons } from './CancellationReasons';
import {
  CancellationReason,
  cancellationReasons,
  CANCELLATION_REASON_OTHER,
} from '../SubscriptionInfo/SubscriptionInfo';

interface CancelModalBodyProps {
  handleCancel: (cancelCode?: string, justification?: string) => Promise<void>;
  nextBillDate: string;
  handleClose: () => void;
  subscriptionUid: string;
  productData: Pick<OrderItemType, 'price' | 'totalPrice' | 'maId' | 'maName'>;
}

export const CancelModalBody = ({
  handleCancel,
  nextBillDate,
  handleClose,
  subscriptionUid,
  productData,
}: CancelModalBodyProps) => {
  const { t } = useTranslation();

  const { isB2CSite, isSpectraBase } = SiteConfig.getConfig();
  const [isGracePeriod, setIsGracePeriod] = useState<boolean>(false);
  const [cancelCode, setCancelCode] = useState<CancellationReason>(
    cancellationReasons[0]
  );
  const [justification, setJustification] = useState<string>('');
  const { loading } = useQuery(GET_IS_GRACE_PERIOD, {
    variables: {
      uid: subscriptionUid,
    },
    skip: !subscriptionUid || isB2CSite,
    onCompleted: (data) => {
      setIsGracePeriod(data?.wIsSubscriptionInGracePeriod?.result);
    },
  });

  const product: OrderItemType = { ...productData } as OrderItemType;

  const action = useCallback(async () => {
    analytic.dataLayerCancelSubscriptionTracker({ product });
    await handleCancel(cancelCode, justification);
    handleClose();
  }, [handleCancel, cancelCode, product, handleClose]);

  return !loading ? (
    <Wrapper
      action={action}
      actionBtnText={t('subDetails.sub.info.cancelCta.modalCta')}
      backBtnText={t('subDetails.sub.info.cancelCta.modalCloseCta')}
      handleClose={handleClose}
      title={isB2CSite ? t('subDetails.sub.info.cancelCta.title') : undefined}
      disableActionButton={
        isB2CSite &&
        (!cancelCode ||
          (cancelCode === CANCELLATION_REASON_OTHER && !justification.trim()))
      }
    >
      <>
        {!isGracePeriod ? (
          <span>
            {t('subDetails.sub.info.cancelCta.modalText1')}
            <span className="text-bold">{nextBillDate}</span>
            {t('subDetails.sub.info.cancelCta.modalText2')}
          </span>
        ) : (
          t('subDetails.sub.info.cancelCta.modalGracePeriod')
        )}
      </>

      {isSpectraBase && (
        <CancellationReasons
          cancelCode={cancelCode}
          setCancelCode={setCancelCode}
          setJustification={setJustification}
        />
      )}
    </Wrapper>
  ) : (
    <Spinner size="64px" />
  );
};
